import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    deletePersist,
    getPersist,
    initialState,
    removeProduct as removeProductFromPersist,
    subscribe as subscribePersist,
    updatePersist,
    utils,
} from '../../../app/cart/persist.js';

/**
 * Support persisting cart data in a cookie, shared between checkoutdwa, embedded cart, and consumer.
 */
const useCartState = () => {
    const [catalog, setCatalog] = useState(initialState.catalog || {});
    const [products, setProducts] = useState(initialState.products);
    const [currency, setCurrency] = useState(initialState.currency);
    const [isCartPanelOpen, setIsCartPanelOpen] = useState(initialState.isCartPanelOpen);

    useEffect(function syncWithCookie() {
        const handler = () => {
            const cookieData = getPersist();
            setCatalog(cookieData.catalog);
            setProducts(cookieData.products || []);
            setCurrency(cookieData.currency);
            setIsCartPanelOpen(cookieData.isCartPanelOpen);
        };

        const unsubscribe = subscribePersist(handler);
        window.addEventListener('focus', handler);
        // pageshow covers bfcache restores, where focus doesn't reliably fire
        window.addEventListener('pageshow', handler);
        handler();

        return () => {
            unsubscribe();
            window.removeEventListener('focus', handler);
            window.removeEventListener('pageshow', handler);
        };
    }, []);

    const updateProductQuantity = useCallback((productCode, selection, newQuantity) => {
        const { products: currentProducts = [] } = getPersist();
        const updatedProducts = utils.setProductQuantity(
            productCode,
            selection,
            newQuantity,
            currentProducts,
        );
        updatePersist({ products: updatedProducts });
    }, []);

    const getProductQuantity = useCallback((productCode, selection) =>
        utils.getProductQuantity(productCode, selection, products),
    [products]);

    const removeProduct = useCallback((productCode, selection) => {
        removeProductFromPersist(productCode, selection);
    }, []);

    // TODO: Hacky way to prevent 0 quantity items - investigate cleaning this up
    const activeProducts = useMemo(
        () => products.filter((product) => product.quantity > 0),
        [products],
    );

    /* TODO: I really want to rename all product related variables here to `items` or `lineItems`.

        Products are the conceptual entities we sell, items is what the customer puts in their cart.

        products => items or lineItems
        getProductQuantity => getItemQuantity
        updateProductQuantity => updateItemQuantity
        removeProduct => removeItem
    */
    return {
        catalog,
        quantity: utils.getTotalQuantity(products), // DEPRECATED: switch bouncieweb to use totalQuantity
        totalQuantity: utils.getTotalQuantity(products),
        products: activeProducts,
        currency,
        isCartPanelOpen,
        updateCartState: updatePersist,
        getProductQuantity,
        updateProductQuantity,
        removeProduct,
        clearCartState: deletePersist,
    };
};

export default useCartState;
